import React from 'react';
import PropTypes from 'prop-types';
import { observer } from 'mobx-react';
import { Dialog, Grid } from '@material-ui/core';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import { useParams } from 'react-router-dom';

import { brandConstants } from 'brand-config';
import ChevronLeftIcon from 'core/assets/svg/ic-chevron-left.svg';
import Button from 'core/components/button/button';
import { Label } from 'core/components/label';
import Field from 'core/components/form/form.v2';
import icInfoIconGreen from 'core/assets/svg/ic-info-dark.svg';
import { NotificationCard } from 'core/components/notification-card';
import {
  getFarmUWInitialValues,
  FarmUWQuestionsCard,
  eligibilityQuestions,
  propertyRiskFactorQuestions,
  operationalDetailsQuestions
} from 'common/components/farm/uw-questions-form';
import DescriptionOfPolicyChanges from '../preview/changes-for-approval/description-of-policy-changes';
import useSession from 'core/hooks/use-session';

import useStyles from './farm-uw-questions-modal.styles';

const allQuestions = [...eligibilityQuestions, ...propertyRiskFactorQuestions, ...operationalDetailsQuestions];

const validationSchema = () => {
  const schemaFields = {};

  // Fields that require details when the answer is 'false' (No)
  const detailsRequiredWhenNo = ['livestockFencing'];

  allQuestions.forEach((question) => {
    schemaFields[question.id] = Yup.string().required(true).nullable();

    // Determine when details are required based on the question
    const detailsRequiredAnswer = detailsRequiredWhenNo.includes(question.id) ? 'false' : 'true';

    schemaFields[`${question.id}Details`] = Yup.string().when(question.id, {
      is: detailsRequiredAnswer,
      then: (schema) => schema.required('Providing details is required'),
      otherwise: (schema) => schema
    });
  });

  // Attestation must be checked
  schemaFields.signedFarmUWAttestation = Yup.boolean()
    .oneOf([true], 'You must attest that your answers are accurate')
    .required('You must attest that your answers are accurate');

  schemaFields.internalNote = Yup.string().required('Please provide a description of the policy changes').nullable();

  return Yup.object().shape(schemaFields);
};

const FarmUWQuestionsModal = ({ open, onClose, onConfirm, preview }) => {
  const classes = useStyles();
  const { canApproveUWReview } = useSession();
  const { id, policyId } = useParams();

  // Get initial values from policy quote data
  const farmUWInitialValues = getFarmUWInitialValues(preview?.uwAnswers);

  return (
    <Dialog open={open} classes={{ paper: classes.dialog }} maxWidth={false}>
      <Formik
        validateOnChange={false}
        validateOnBlur={false}
        initialValues={{
          ...farmUWInitialValues,
          internalNote: preview?.internalNote
        }}
        validationSchema={validationSchema}
        onSubmit={(values) =>
          onConfirm({
            ...values,
            previewId: preview.id,
            policyPreviewUsername: preview.createdBy.username
          })
        }
      >
        {() => {
          return (
            <Form>
              <Grid container spacing={6} direction="column">
                <Grid item>
                  <div className={classes.backLink} onClick={onClose}>
                    <img src={ChevronLeftIcon} alt="" className={classes.backIcon} />
                    Go back to policy changes preview
                  </div>
                </Grid>
                <Grid item>
                  <Label type="h1">
                    {' '}
                    {canApproveUWReview
                      ? 'These changes require your review.'
                      : 'Please submit these changes for approval.'}
                  </Label>
                </Grid>
                <Grid item>
                  {canApproveUWReview ? (
                    <Grid container spacing={4}>
                      <Grid item>
                        <Label className={classes.contentLabel} type="body1">
                          These changes impact locations or risks. Please verify the policy’s eligibility based on these
                          questions.
                        </Label>
                      </Grid>
                    </Grid>
                  ) : (
                    <Grid container spacing={4}>
                      <Grid item>
                        <Label className={classes.contentLabel} type="body1">
                          Because these changes impact risks or locations, these policy changes will require approval
                          from the {brandConstants.SHORT_NAME} Underwriting Processing Team before they can be applied
                          to the policy.
                        </Label>
                      </Grid>
                      <Grid item>
                        <Label className={classes.contentLabel} type="body1">
                          Please review and complete the following requirements before submitting these changes for
                          approval:
                        </Label>
                      </Grid>
                    </Grid>
                  )}
                </Grid>

                {/* UW Questions */}
                <Grid item>
                  <FarmUWQuestionsCard questions={eligibilityQuestions} label="Eligibility history" variant="policy" />
                </Grid>
                <Grid item>
                  <FarmUWQuestionsCard
                    questions={propertyRiskFactorQuestions}
                    label="Property risk factors"
                    variant="policy"
                  />
                </Grid>
                <Grid item>
                  <FarmUWQuestionsCard
                    questions={operationalDetailsQuestions}
                    label="Operational details"
                    variant="policy"
                  />
                </Grid>

                {/* Attestation */}
                <Grid item xs={12}>
                  <Field
                    id="signedFarmUWAttestation"
                    name="signedFarmUWAttestation"
                    type="checkbox"
                    label="I attest that the answers to these questions are true and accurate to the best of my knowledge."
                    mode="light"
                  />
                </Grid>
              </Grid>
              {canApproveUWReview ? (
                <>
                  <NotificationCard className={classes.notification} type="infoGreen" hideIcon>
                    <Grid container direction="column">
                      <Grid item className={classes.infoIconContainer}>
                        <img src={icInfoIconGreen} alt="Info Icon" className={classes.infoIcon} />
                        <Label type="body1Bold">
                          The new declarations page will generate after saving this change.
                        </Label>
                      </Grid>
                      <Label type="body1" className={classes.infoText}>
                        If you'd like to view the declarations page of the current policy's version{' '}
                        <a
                          href={`${window.location.origin}/customer/${id}/policy/${policyId}?tab=documents`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className={classes.reviewLink}
                        >
                          you may do so here
                        </a>
                        .
                      </Label>
                    </Grid>
                  </NotificationCard>
                  <Grid container alignItems="center" justifyContent="space-between" className={classes.notesContainer}>
                    <Field
                      id="internalNote"
                      name="internalNote"
                      type="string"
                      label="Add an internal note"
                      mode="light"
                      xs={10}
                      ignoreGlobalDisabledState
                      permissions={{ isLicensedAction: false }}
                    />
                    <Button
                      id="submit"
                      name="submit"
                      type="submit"
                      variant="contained"
                      label="Add an internal note"
                      mode="light"
                      xs={4}
                      color="primary"
                      className={classes.submit}
                    >
                      Save policy changes
                    </Button>
                  </Grid>
                </>
              ) : (
                <>
                  <DescriptionOfPolicyChanges variant="farm" />
                  <Grid container justifyContent="flex-end" spacing={2} className={classes.buttonContainer}>
                    <Grid item>
                      <Button className={classes.button} variant="contained" color="primary" type="submit">
                        Submit changes for approval
                      </Button>
                    </Grid>
                  </Grid>
                </>
              )}
            </Form>
          );
        }}
      </Formik>
    </Dialog>
  );
};

FarmUWQuestionsModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onConfirm: PropTypes.func,
  preview: PropTypes.object
};

FarmUWQuestionsModal.defaultProps = {
  onConfirm: undefined,
  preview: null
};

export default observer(FarmUWQuestionsModal);
